import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Statistic, Progress, Table, Tag, Typography, Spin, Alert } from 'antd'
import {
  ApiOutlined,
  DollarOutlined,
  RiseOutlined,
  UserOutlined,
  CheckCircleOutlined,
  RobotOutlined,
  LineChartOutlined,
} from '@ant-design/icons'
import { Line, Pie, Column } from '@ant-design/charts'
import { metricsAPI } from '../services/api'

const { Title, Text } = Typography

const Dashboard = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [metrics, setMetrics] = useState(null)
  const isMobile = window.innerWidth < 768

  useEffect(() => {
    fetchMetrics()
  }, [])

  const fetchMetrics = async () => {
    try {
      setLoading(true)
      const data = await metricsAPI.getDashboardMetrics()
      setMetrics(data)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch dashboard metrics:', err)
      setError('Failed to load dashboard metrics')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spin size="large" tip="Loading dashboard metrics..." />
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <button onClick={fetchMetrics} className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700">
            Retry
          </button>
        }
      />
    )
  }

  if (!metrics) {
    return <Alert message="No data available" type="info" />
  }

  // Prepare chart data from API response with safe defaults
  const subscriptionDistribution = metrics.subscription_distribution || []
  const usageTrend = metrics.usage_trend || []
  const topEndpoints = metrics.top_endpoints || []

  // Safe access to nested metrics with defaults
  const displayMetrics = {
    api: {
      total_subscribers: metrics.api?.total_subscribers || 0,
      active_keys: metrics.api?.active_keys || 0,
      total_calls: metrics.api?.total_calls || 0,
      avg_latency: metrics.api?.avg_latency || 0,
      requests_month: metrics.api?.requests_month || 0,
      requests_today: metrics.api?.requests_today || 0,
      uptime: metrics.api?.uptime || 99.9,
      avg_response_time: metrics.api?.avg_response_time || 0,
    },
    revenue: {
      mrr: metrics.revenue?.mrr || 0,
      growth: metrics.revenue?.growth || 0,
      trial_signups: metrics.revenue?.trial_signups || 0,
      conversions: metrics.revenue?.conversions || 0,
      churn_rate: metrics.revenue?.churn_rate || 0,
    },
    ml: {
      total_models: metrics.ml?.total_models || 0,
      active_deployments: metrics.ml?.active_deployments || 0,
    },
    models: {
      active: metrics.models?.active || 0,
    },
    system: {
      uptime: metrics.system?.uptime || 0,
      health_status: metrics.system?.health_status || 'unknown',
    },
  }

  // Revenue trend - use mock data for now, will be enhanced later
  const revenueData = [
    { month: 'Jul', revenue: Math.max(0, displayMetrics.revenue.mrr - 14200) },
    { month: 'Aug', revenue: Math.max(0, displayMetrics.revenue.mrr - 10900) },
    { month: 'Sep', revenue: Math.max(0, displayMetrics.revenue.mrr - 7600) },
    { month: 'Oct', revenue: Math.max(0, displayMetrics.revenue.mrr - 5100) },
    { month: 'Nov', revenue: Math.max(0, displayMetrics.revenue.mrr - 2500) },
    { month: 'Dec', revenue: displayMetrics.revenue.mrr },
  ]

  const endpointColumns = [
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      render: (text) => <Text code>{text}</Text>,
    },
    {
      title: 'Calls (30d)',
      dataIndex: 'calls',
      key: 'calls',
      align: 'right',
      render: (value) => value.toLocaleString(),
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      render: (tier) => {
        const color = tier === 'Enterprise' ? 'gold' : tier === 'Business+' ? 'blue' : 'green'
        return <Tag color={color}>{tier}</Tag>
      },
    },
  ]

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>API Platform Dashboard</Title>
        <Text type="secondary">Real-time metrics</Text>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title="API Subscribers"
              value={displayMetrics.api.total_subscribers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#0284c7' }}
            />
            <Progress
              percent={(displayMetrics.api.active_keys / Math.max(1, displayMetrics.api.total_subscribers) * 100)}
              strokeColor="#0284c7"
              showInfo={false}
            />
            <Text type="secondary" className="text-xs">
              {displayMetrics.api.active_keys} Active Keys
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title="Monthly Revenue"
              value={displayMetrics.revenue.mrr}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#10b981' }}
              precision={0}
            />
            <Progress
              percent={(displayMetrics.revenue.mrr / 50000 * 100)}
              strokeColor="#10b981"
              showInfo={false}
            />
            <Text type="secondary" className="text-xs">
              Target: $50,000
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title="API Requests (30d)"
              value={displayMetrics.api.requests_month}
              prefix={<ApiOutlined />}
              valueStyle={{ color: '#8b5cf6' }}
            />
            <Progress
              percent={85}
              strokeColor="#8b5cf6"
              showInfo={false}
            />
            <Text type="secondary" className="text-xs">
              {displayMetrics.api.requests_today.toLocaleString()} Today
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="dashboard-card">
            {displayMetrics.api.uptime !== null ? (
              <>
                <Statistic
                  title="API Uptime"
                  value={displayMetrics.api.uptime}
                  prefix={<CheckCircleOutlined />}
                  suffix="%"
                  valueStyle={{ color: '#10b981' }}
                  precision={2}
                />
                <Progress
                  percent={displayMetrics.api.uptime}
                  strokeColor="#10b981"
                  showInfo={false}
                />
                <Text type="secondary" className="text-xs">
                  {displayMetrics.api.avg_response_time}ms avg response
                </Text>
              </>
            ) : (
              <>
                <Statistic
                  title="API Uptime"
                  value="N/A"
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#9ca3af' }}
                />
                <Text type="secondary" className="text-xs">
                  No API requests recorded yet
                </Text>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Secondary Metrics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Active AI Models"
              value={displayMetrics.models.active}
              prefix={<RobotOutlined />}
              valueStyle={{ fontSize: '24px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Trial Signups (30d)"
              value={displayMetrics.revenue.trial_signups}
              prefix={<RiseOutlined />}
              valueStyle={{ fontSize: '24px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Trial Conversions"
              value={displayMetrics.revenue.conversions}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ fontSize: '24px', color: '#10b981' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Churn Rate"
              value={displayMetrics.revenue.churn_rate}
              suffix="%"
              prefix={<LineChartOutlined />}
              valueStyle={{ fontSize: '24px', color: '#ef4444' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} lg={12}>
          <Card title="Revenue Trend (MRR)" className="dashboard-card">
            <Line
              data={revenueData}
              xField="month"
              yField="revenue"
              smooth={true}
              point={{ size: 5, shape: 'diamond' }}
              color="#10b981"
              height={280}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="API Requests (Last 7 Days)" className="dashboard-card">
            {usageTrend.length > 0 ? (
              <Column
                data={usageTrend}
                xField="day"
                yField="requests"
                color="#0284c7"
                height={280}
              />
            ) : (
              <div className="flex items-center justify-center h-[280px] text-gray-500">
                No API requests recorded yet
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} lg={8}>
          <Card title="Subscription Tiers" className="dashboard-card">
            {subscriptionDistribution.length > 0 ? (
              <Pie
                data={subscriptionDistribution}
                angleField="value"
                colorField="type"
                radius={0.8}
                innerRadius={0.5}
                label={{
                  type: 'outer',
                  content: ({ type, value }) => `${type}: ${value}`,
                }}
                legend={{
                  position: 'bottom',
                }}
                height={280}
                statistic={{
                  title: {
                    content: 'Total',
                  },
                  content: {
                    content: subscriptionDistribution.reduce((sum, item) => sum + item.value, 0).toString(),
                  },
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-[280px] text-gray-500">
                No subscription data available
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card title="Top API Endpoints (30 days)" className="dashboard-card">
            <Table
              columns={endpointColumns}
              dataSource={topEndpoints}
              rowKey="endpoint"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard
